import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DecimalPipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { ShareGanasModalComponent } from '../../../shared/modal/share-ganas-modal/share-ganas-modal.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartComponent } from 'ng-apexcharts';
import { BaseChartOptions, DEFAULT_CHART_OPTIONS } from 'shared/types/chart.config';
import { NgScrollbar } from 'ngx-scrollbar';
import { AuthService } from 'core';
import { ThousandsSeparatorPipe } from 'shared';
import { BalanceService } from 'core';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';
import { BalanceMoneyPipe } from 'shared/pipes/balance-money.pipe';

@Component({
  selector: 'app-balance-members',
  imports: [
    TranslatePipe,
    KorviuButtonComponent,
    ChartComponent,
    NgScrollbar,
    ThousandsSeparatorPipe,
    BalanceMoneyPipe,
    DecimalPipe,
    ResourceLoadErrorComponent
  ],
  templateUrl: './balance-members.component.html',
  styleUrl: './balance-members.component.scss'
})
export class BalanceMembersComponent {
  readonly #modalService = inject(NzModalService);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #authService = inject(AuthService);
  readonly #balanceService = inject(BalanceService);
  readonly #translateService = inject(TranslateService);

  readonly language = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  });

  readonly memberSummaryCardsResource = this.#balanceService.getMemberSummaryCards();
  readonly memberSummaryCards = computed(() =>
    this.memberSummaryCardsResource.hasValue() ? this.memberSummaryCardsResource.value() : undefined
  );

  readonly recentEarningsResource = this.#balanceService.getRecentEarnings();
  readonly recentEarnings = computed(() =>
    this.recentEarningsResource.hasValue() ? this.recentEarningsResource.value() : undefined
  );
  readonly showRecentEarnings = computed(
    () => this.recentEarningsResource.status() === 'resolved' && !!this.recentEarnings()?.amount
  );
  readonly recentEarningsSign = computed(() => ((this.recentEarnings()?.amount ?? 0) > 0 ? '+' : ''));

  readonly earningsSummaryResource = this.#balanceService.getEarningsSummary();
  readonly earningsSummary = computed(() =>
    this.earningsSummaryResource.hasValue() ? this.earningsSummaryResource.value() : undefined
  );
  readonly earningsSummaryResolved = computed(() => this.earningsSummaryResource.status() === 'resolved');

  readonly earningsSummaryMonths = computed(() => this.earningsSummary()?.months || []);
  readonly earningsTrendPercentage = computed(() => Math.abs(this.earningsSummary()?.trend_percentage ?? 0));
  readonly earningsTrendDirection = computed(() => this.earningsSummary()?.trend_direction?.toLowerCase() || 'neutral');
  readonly earningsTrendTranslationKey = computed(() => {
    if (this.earningsTrendDirection() === 'up') {
      return 'my-forest.trendingUpBy';
    }

    if (this.earningsTrendDirection() === 'down') {
      return 'my-forest.trendingDownBy';
    }

    return 'my-forest.trendUnchanged';
  });
  readonly earningsTrendColor = computed(() => (this.earningsTrendDirection() === 'down' ? '#D14343' : '#078339'));
  readonly hasDirectionalEarningsTrend = computed(() => ['up', 'down'].includes(this.earningsTrendDirection()));
  readonly currentUser = computed(() => this.#authService.currentUser$());
  readonly isUserRhondasNichols = computed(() => this.currentUser()?.id === 5);
  readonly availableGanas = this.#balanceService.balanceGanas;
  readonly cardSpending = computed(() => this.memberSummaryCards()?.card_spending ?? 0);
  readonly totalLeaves = computed(() => this.memberSummaryCards()?.total_leaves || 0);
  readonly growingGanas = computed(() => this.memberSummaryCards()?.growing_ganas || 0);

  readonly ganasEarned = computed<BaseChartOptions>(() => {
    const months = this.earningsSummaryMonths() || [];
    const categories: string[] = months.map(m => m.month);
    const seriesData: number[] = months.map(m => m.total);

    const barHeight = 32;
    const gap = 0;
    const numberOfBars = categories.length;
    const chartHeight = barHeight * numberOfBars + gap * (numberOfBars - 1);
    return {
      ...DEFAULT_CHART_OPTIONS,
      chart: {
        ...DEFAULT_CHART_OPTIONS.chart,
        type: 'bar',
        height: chartHeight,
        width: '100%'
      },
      states: {
        /* normal: {
          filter: {
            type: 'none'
          }
        }, */
        hover: {
          filter: {
            type: 'none'
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none'
          }
        }
      },
      series: [
        {
          name: 'Earnings',
          data: seriesData
        }
      ],
      xaxis: {
        ...DEFAULT_CHART_OPTIONS.xaxis,
        categories,
        labels: {
          show: true,
          style: {
            fontSize: '16px',
            colors: '#737980',
            fontFamily: 'Outfit'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        ...DEFAULT_CHART_OPTIONS.yaxis,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },

      plotOptions: {
        ...DEFAULT_CHART_OPTIONS.plotOptions,
        bar: {
          ...DEFAULT_CHART_OPTIONS.plotOptions.bar,
          horizontal: false,
          borderRadius: 4,
          columnWidth: '48px',
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        ...DEFAULT_CHART_OPTIONS.dataLabels,
        enabled: true,
        offsetY: -25,
        formatter: (val: number | string) => {
          return parseInt(String(val), 10).toLocaleString();
        },
        style: {
          fontSize: '16px',
          fontFamily: 'Outfit',
          fontWeight: '400',
          colors: ['#505760']
        }
      },
      tooltip: {
        ...DEFAULT_CHART_OPTIONS.tooltip,
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex][dataPointIndex] ?? 0;
          const category = w.config.xaxis?.categories[dataPointIndex] ?? '';
          const displayValue = parseInt(value, 10).toLocaleString();
          return `
					<div class="px-3 py-2 flex flex-col custom-tooltip">
					  <div class="text-sm md:text-base font-medium outfit title">${category}</div>
					  <div class="paragraph-small-card font-normal outfit value">${displayValue}</div>
					</div>
				  `;
        }
      },
      responsive: [
        {
          breakpoint: 430,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
                barHeight: `${barHeight}px`
              }
            },
            dataLabels: {
              enabled: true,
              offsetX: 35,
              offsetY: 0,
              formatter: (val: number | string) => parseInt(String(val), 10).toLocaleString(),
              style: {
                fontSize: '14px',
                fontFamily: 'Outfit',
                fontWeight: '400',
                colors: ['#505760']
              }
            },
            xaxis: {
              labels: {
                show: false
              }
            },
            yaxis: {
              labels: {
                show: true,
                style: {
                  fontSize: '14px',
                  fontFamily: 'Outfit'
                }
              }
            }
          }
        }
      ]
    };
  });

  getOkeeyCash(): void {
    this.#router.navigate(['/workspace/my-okeey-cash/apply']);
  }

  redeem(): void {
    this.#router.navigate(['/workspace/rewards']);
  }

  shareGanas(): void {
    this.#modalService.create({
      nzContent: ShareGanasModalComponent,
      nzWrapClassName: 'modal-wrap',
      nzClosable: false,
      nzWidth: 555,
      nzClassName: 'modal-dialog'
    });
  }

  viewEarnings(): void {
    this.#router.navigate(['earnings'], { relativeTo: this.#activatedRoute });
  }

  viewTransactions(): void {
    this.#router.navigate(['transactions'], { relativeTo: this.#activatedRoute });
  }

  cardDetails(): void {
    this.#router.navigate(['card-details'], { relativeTo: this.#activatedRoute });
  }
}
